import {
  DEFAULT_RULES,
  TraceProcessor,
  computeSplits,
  currentPace,
  type AcceptedSegment,
  type GameRules,
  type Split,
  type TracePoint,
} from '@rw/game-engine';
import { segmentCellDistances, type HexGrid } from '@rw/geo';
import type { LocalRun, RunStore, StoredPoint } from '../../../services/run-store';
import type { GeoErrorKind, GeoFix, GeoSource } from './geo-source';

export type TrackingState =
  | 'IDLE'
  | 'STARTING'
  | 'RUNNING'
  | 'PAUSED'
  | 'RESUMING'
  | 'FINISHING'
  | 'COMPLETED'
  | 'FAILED';

export type GpsStatus = 'off' | 'searching' | 'good' | 'fair' | 'poor' | 'denied' | 'unavailable';

export interface TrackingSnapshot {
  state: TrackingState;
  runId: string | null;
  regionId: string | null;
  startedAt: number | null;
  distanceM: number;
  /** Active time at the moment of the snapshot (excludes pauses). */
  activeMs: number;
  runningSince: number | null;
  gpsStatus: GpsStatus;
  gpsError: string | null;
  position: { lat: number; lng: number; accuracy: number; ts: number } | null;
  /** Cleaned route, one line per continuous sub-trace, [lng, lat]. */
  route: Array<Array<[number, number]>>;
  routeVersion: number;
  currentCell: string | null;
  currentCellMeters: number;
  /** Provisional captures (≥ capture threshold); confirmed by the server at finish. */
  capturedCells: string[];
  inRegion: boolean;
  /** Rolling pace over the last ~30 s of movement; null when stopped. */
  currentPaceSecPerKm: number | null;
  /** Completed 1 km splits so far. */
  splits: Split[];
  pointCount: number;
  storageDurable: boolean;
  storageError: string | null;
}

export type EngineEvent =
  | { type: 'capture'; gridKey: string }
  | { type: 'split'; split: Split }
  | { type: 'cell'; gridKey: string }
  | { type: 'point'; point: StoredPoint }
  | { type: 'state'; state: TrackingState };

export interface RegionContext {
  regionId: string;
  grid: HexGrid;
  isPlayable: (gridKey: string) => boolean;
}

export interface TrackingEngineDeps {
  createSource: () => GeoSource;
  store: RunStore;
  rules?: GameRules;
  uuid?: () => string;
}

export function gpsStatusFor(accuracy: number, rules: GameRules = DEFAULT_RULES): GpsStatus {
  if (accuracy <= rules.gps.maxAccuracyForCaptureM) return 'good';
  if (accuracy <= rules.gps.maxAccuracyForDistanceM) return 'fair';
  return 'poor';
}

/**
 * GPS tracking state machine. Owns no UI. Every accepted fix is persisted to the
 * RunStore before the snapshot is published, so a crash, reload or network loss
 * never loses a run.
 */
export class TrackingEngine {
  private source: GeoSource | null = null;
  private run: LocalRun | null = null;
  private region: RegionContext | null = null;
  private processor: TraceProcessor;
  private cellMeters = new Map<string, number>();
  private disqualified = new Set<string>();
  private listeners = new Set<(s: TrackingSnapshot) => void>();
  private eventListeners = new Set<(e: EngineEvent) => void>();
  private writeChain: Promise<void> = Promise.resolve();
  private unsaved: StoredPoint[] = [];
  private snap: TrackingSnapshot;
  private readonly rules: GameRules;
  private readonly uuid: () => string;

  constructor(private readonly deps: TrackingEngineDeps) {
    this.rules = deps.rules ?? DEFAULT_RULES;
    this.uuid = deps.uuid ?? (() => crypto.randomUUID());
    this.processor = new TraceProcessor({ rules: this.rules });
    this.snap = this.emptySnapshot();
  }

  get snapshot(): TrackingSnapshot {
    return this.snap;
  }

  get currentRun(): LocalRun | null {
    return this.run;
  }

  get sourceKind() {
    return this.source?.kind ?? null;
  }

  now(): number {
    return this.source?.now() ?? Date.now();
  }

  subscribe(fn: (s: TrackingSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snap);
    return () => this.listeners.delete(fn);
  }

  on(fn: (e: EngineEvent) => void): () => void {
    this.eventListeners.add(fn);
    return () => this.eventListeners.delete(fn);
  }

  setRegion(region: RegionContext) {
    this.region = region;
  }

  /** Start listening to GPS (pre-run preview) without recording. */
  warmUp() {
    if (this.source) return;
    this.source = this.deps.createSource();
    this.patch({ gpsStatus: 'searching', gpsError: null });
    this.source.start(
      (fix) => this.onFix(fix),
      (kind, msg) => this.onGpsError(kind, msg),
    );
  }

  /** Stop GPS when idle (e.g. leaving the run screen without a run). */
  coolDown() {
    if (this.run) return;
    this.source?.stop();
    this.source = null;
    this.patch({ gpsStatus: 'off' });
  }

  async start(userId: string): Promise<LocalRun> {
    if (this.run && this.snap.state !== 'COMPLETED' && this.snap.state !== 'FAILED') {
      throw new Error('A run is already in progress');
    }
    if (!this.region) throw new Error('Game region not loaded');
    this.setState('STARTING');
    this.warmUp();
    this.source?.onRecordingStarted?.();
    this.resetTrace();
    const now = this.now();
    const run: LocalRun = {
      id: this.uuid(),
      userId,
      regionId: this.region.regionId,
      startedAt: now,
      endedAt: null,
      status: 'active',
      segment: 0,
      nextSeq: 0,
      clientDistanceM: 0,
      activeMs: 0,
      runningSince: now,
      result: null,
      error: null,
      updatedAt: now,
    };
    try {
      await this.deps.store.putRun(run);
    } catch (e) {
      this.setState('FAILED');
      throw new Error(`Could not save the run on this device: ${(e as Error).message}`);
    }
    this.run = run;
    this.patch({
      runId: run.id,
      regionId: run.regionId,
      startedAt: run.startedAt,
      runningSince: now,
      activeMs: 0,
      storageDurable: this.deps.store.durable,
    });
    this.setState('RUNNING');
    return run;
  }

  async pause() {
    if (!this.run || (this.snap.state !== 'RUNNING' && this.snap.state !== 'RESUMING')) return;
    const now = this.now();
    const tail = this.processor.flush();
    if (tail) this.onSegment(tail);
    this.run = {
      ...this.run,
      status: 'paused',
      activeMs: this.run.activeMs + (this.run.runningSince ? now - this.run.runningSince : 0),
      runningSince: null,
      clientDistanceM: this.processor.distanceM,
    };
    await this.persistRun();
    this.patch({ activeMs: this.run.activeMs, runningSince: null, distanceM: this.processor.distanceM, currentPaceSecPerKm: null });
    this.setState('PAUSED');
  }

  async resume() {
    if (!this.run || this.snap.state !== 'PAUSED') return;
    this.warmUp();
    const now = this.now();
    this.run = { ...this.run, status: 'active', segment: this.run.segment + 1, runningSince: now };
    await this.persistRun();
    this.patch({ runningSince: now });
    // RESUMING until the first fix of the new segment arrives.
    this.setState('RESUMING');
  }

  async finish(): Promise<LocalRun> {
    if (!this.run) throw new Error('No active run');
    const prev = this.snap.state;
    if (prev !== 'RUNNING' && prev !== 'PAUSED' && prev !== 'RESUMING') throw new Error(`Cannot finish from ${prev}`);
    this.setState('FINISHING');
    const now = this.now();
    const tail = this.processor.flush();
    if (tail) this.onSegment(tail);
    await this.writeChain;
    await this.flushUnsaved();
    this.run = {
      ...this.run,
      status: 'finished',
      endedAt: now,
      activeMs: this.run.activeMs + (this.run.runningSince ? now - this.run.runningSince : 0),
      runningSince: null,
      clientDistanceM: this.processor.distanceM,
    };
    await this.persistRun();
    const finished = this.run;
    this.source?.stop();
    this.source = null;
    this.patch({
      activeMs: finished.activeMs,
      runningSince: null,
      distanceM: this.processor.distanceM,
      gpsStatus: 'off',
    });
    this.setState('COMPLETED');
    return finished;
  }

  /** Throws away the current run locally (only allowed before it is finished). */
  async discard() {
    if (!this.run) return;
    const id = this.run.id;
    this.run = null;
    await this.writeChain.catch(() => undefined);
    await this.deps.store.deleteRun(id);
    this.reset();
  }

  /** Back to IDLE after a completed run has been handed to sync. */
  reset() {
    this.run = null;
    this.resetTrace();
    const gps = this.source ? this.snap.gpsStatus : 'off';
    this.snap = { ...this.emptySnapshot(), gpsStatus: gps, position: this.snap.position };
    this.setState('IDLE');
  }

  /** Restores an interrupted run (reload, crash, OS kill) as PAUSED from local storage. */
  async recover(run: LocalRun): Promise<void> {
    this.resetTrace();
    const points = await this.deps.store.getPoints(run.id);
    for (const p of points) this.ingest(p);
    const tail = this.processor.flush();
    if (tail) this.onSegment(tail);
    // Time spent while the app was closed does not count as running time.
    const lastTs = points[points.length - 1]?.ts ?? run.runningSince ?? run.startedAt;
    const activeMs = run.activeMs + (run.runningSince ? Math.max(0, lastTs - run.runningSince) : 0);
    this.run = { ...run, status: 'paused', runningSince: null, activeMs, nextSeq: Math.max(run.nextSeq, (points[points.length - 1]?.seq ?? -1) + 1) };
    await this.persistRun();
    this.patch({
      runId: run.id,
      regionId: run.regionId,
      startedAt: run.startedAt,
      activeMs,
      runningSince: null,
      distanceM: this.processor.distanceM,
      pointCount: points.length,
      storageDurable: this.deps.store.durable,
    });
    this.setState('PAUSED');
  }

  /** Resolves once every recorded point so far has been written to storage. */
  async whenSaved(): Promise<void> {
    await this.writeChain;
  }

  activeMsAt(now = this.now()): number {
    return this.snap.activeMs + (this.snap.runningSince ? Math.max(0, now - this.snap.runningSince) : 0);
  }

  // ───────────────────────────── internals ─────────────────────────────

  private onFix(fix: GeoFix) {
    const position = { lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy, ts: fix.ts };
    const cell = this.region ? this.region.grid.keyAt(fix) : null;
    const inRegion = cell ? this.region!.isPlayable(cell) : false;
    const changes: Partial<TrackingSnapshot> = {
      position,
      gpsStatus: gpsStatusFor(fix.accuracy, this.rules),
      gpsError: null,
      inRegion,
    };
    const state = this.snap.state;
    if (this.run && (state === 'RUNNING' || state === 'RESUMING')) {
      const point: StoredPoint = {
        runId: this.run.id,
        seq: this.run.nextSeq,
        segment: this.run.segment,
        lat: fix.lat,
        lng: fix.lng,
        ts: fix.ts,
        accuracy: fix.accuracy,
        altitude: fix.altitude,
        speed: fix.speed,
      };
      this.run = { ...this.run, nextSeq: this.run.nextSeq + 1 };
      this.ingest(point);
      this.run.clientDistanceM = this.processor.distanceM;
      this.persistPoint(point);
      changes.distanceM = this.processor.distanceM;
      changes.pointCount = this.run.nextSeq;
      changes.currentPaceSecPerKm = currentPace(this.processor.segments, fix.ts);
      if (cell && cell !== this.snap.currentCell) this.emit({ type: 'cell', gridKey: cell });
      changes.currentCell = cell;
      changes.currentCellMeters = cell ? Math.round(this.cellMeters.get(cell) ?? 0) : 0;
      if (state === 'RESUMING') {
        this.patch(changes);
        this.setState('RUNNING');
        return;
      }
    } else if (!this.run || state === 'IDLE') {
      changes.currentCell = cell;
    }
    this.patch(changes);
  }

  private onGpsError(kind: GeoErrorKind, message: string) {
    const gpsStatus: GpsStatus = kind === 'denied' ? 'denied' : kind === 'timeout' ? 'searching' : 'unavailable';
    this.patch({ gpsStatus, gpsError: message || kind });
  }

  /** Feed a point through the shared trace processor and update captures/route. */
  private ingest(point: TracePoint) {
    const result = this.processor.push(point);
    if (result.rejection === 'GPS_JUMP' && this.region) {
      for (const p of this.processor.jumpPoints.slice(-2)) this.disqualified.add(this.region.grid.keyAt(p));
    }
    if (result.segment) this.onSegment(result.segment);
  }

  private onSegment(seg: AcceptedSegment) {
    // Route rendering
    const route = this.snap.route;
    const last = route[route.length - 1];
    const lastPt = last?.[last.length - 1];
    if (!last || !lastPt || lastPt[0] !== seg.from.lng || lastPt[1] !== seg.from.lat) {
      route.push([[seg.from.lng, seg.from.lat], [seg.to.lng, seg.to.lat]]);
    } else last.push([seg.to.lng, seg.to.lat]);
    this.snap = { ...this.snap, routeVersion: this.snap.routeVersion + 1, distanceM: this.processor.distanceM };

    // Kilometre splits
    const full = computeSplits(this.processor.segments).filter((sp) => sp.complete);
    if (full.length > this.snap.splits.length) {
      for (const sp of full.slice(this.snap.splits.length)) this.emit({ type: 'split', split: sp });
      this.snap = { ...this.snap, splits: full };
    }

    // Provisional capture accounting — same rule as the server.
    if (!this.region) return;
    const maxAcc = this.rules.gps.maxAccuracyForCaptureM;
    if (seg.from.accuracy > maxAcc || seg.to.accuracy > maxAcc) return;
    const parts = segmentCellDistances(this.region.grid, seg.from, seg.to, this.rules.capture.sampleStepM);
    const captured = new Set(this.snap.capturedCells);
    let changed = false;
    for (const [key, meters] of parts) {
      const total = (this.cellMeters.get(key) ?? 0) + meters;
      this.cellMeters.set(key, total);
      if (
        total >= this.rules.capture.minDistanceInsideM &&
        !captured.has(key) &&
        !this.disqualified.has(key) &&
        this.region.isPlayable(key)
      ) {
        captured.add(key);
        changed = true;
        this.emit({ type: 'capture', gridKey: key });
      }
    }
    if (changed) this.snap = { ...this.snap, capturedCells: [...captured] };
  }

  private persistPoint(point: StoredPoint) {
    const run = this.run!;
    this.writeChain = this.writeChain
      .then(async () => {
        await this.flushUnsaved();
        await this.deps.store.appendPoint(run, point);
        this.emit({ type: 'point', point });
        if (this.snap.storageError) this.patch({ storageError: null });
      })
      .catch((e: Error) => {
        // Keep the point in memory and retry on the next write.
        this.unsaved.push(point);
        this.patch({ storageError: `Could not save GPS data: ${e.message}` });
      });
  }

  private async flushUnsaved() {
    while (this.unsaved.length && this.run) {
      const p = this.unsaved[0]!;
      await this.deps.store.appendPoint(this.run, p);
      this.unsaved.shift();
      this.emit({ type: 'point', point: p });
    }
  }

  private async persistRun() {
    if (!this.run) return;
    const run = this.run;
    this.writeChain = this.writeChain.then(() => this.deps.store.putRun(run)).catch((e: Error) => {
      this.patch({ storageError: `Could not save run: ${e.message}` });
    });
    await this.writeChain;
  }

  private resetTrace() {
    this.processor = new TraceProcessor({ rules: this.rules });
    this.cellMeters = new Map();
    this.disqualified = new Set();
    this.unsaved = [];
    this.snap = {
      ...this.snap,
      route: [],
      routeVersion: this.snap.routeVersion + 1,
      capturedCells: [],
      distanceM: 0,
      pointCount: 0,
      splits: [],
      currentPaceSecPerKm: null,
    };
  }

  private setState(state: TrackingState) {
    this.patch({ state });
    this.emit({ type: 'state', state });
  }

  private patch(changes: Partial<TrackingSnapshot>) {
    this.snap = { ...this.snap, ...changes };
    for (const l of this.listeners) l(this.snap);
  }

  private emit(e: EngineEvent) {
    for (const l of this.eventListeners) l(e);
  }

  private emptySnapshot(): TrackingSnapshot {
    return {
      state: 'IDLE',
      runId: null,
      regionId: null,
      startedAt: null,
      distanceM: 0,
      activeMs: 0,
      runningSince: null,
      gpsStatus: 'off',
      gpsError: null,
      position: null,
      route: [],
      routeVersion: 0,
      currentCell: null,
      currentCellMeters: 0,
      capturedCells: [],
      inRegion: false,
      currentPaceSecPerKm: null,
      splits: [],
      pointCount: 0,
      storageDurable: true,
      storageError: null,
    };
  }
}
